// src/components/AssignSponsorsModal.js
// Updated: 11/09/2025 08:56 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
// --- 1. Import 'functions' and 'httpsCallable' ---
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  getDocs,
  // --- 2. Remove 'doc', 'updateDoc', 'arrayUnion', 'arrayRemove' ---
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { X, ShieldCheck, UserMinus } from 'lucide-react';

const LoadingSpinner = () => <div className="h-5 w-5 animate-spin rounded-full border-2 border-dashed border-blue-500"></div>;

// --- 3. Define new callable function ---
const adminManageSponsors = httpsCallable(functions, 'adminManageSponsors');

export default function AssignSponsorsModal({ isOpen, onClose, session }) {
  const [orgUsers, setOrgUsers] = useState([]);
  const [assignedSponsorIds, setAssignedSponsorIds] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const { userData } = useAuth(); // Needed for orgId

  // Fetch users (No changes)
  useEffect(() => {
    if (!isOpen || !userData?.orgId) {
      setOrgUsers([]);
      return;
    };
    setIsLoadingUsers(true);
    setError('');
    const usersQuery = query(
      collection(db, 'users'),
      where('orgId', '==', userData.orgId)
    );
    getDocs(usersQuery)
      .then((querySnapshot) => {
        const users = [];
        querySnapshot.forEach((doc) => {
          users.push({ id: doc.id, ...doc.data() });
        });
        setOrgUsers(users);
        setAssignedSponsorIds(session?.sessionSponsors || []);
      })
      .catch((err) => {
        console.error("Error fetching organization users:", err);
        setError("Failed to load users.");
      })
      .finally(() => {
        setIsLoadingUsers(false);
      });
  }, [isOpen, userData?.orgId, session]);

  // --- 4. REWRITE 'toggleSponsor' to use the callable function ---
  const toggleSponsor = async (userId) => {
    if (isUpdating || !session?.id) return;

    setIsUpdating(true);
    setError('');
    const isCurrentlyAssigned = assignedSponsorIds.includes(userId);
    const action = isCurrentlyAssigned ? 'remove' : 'add';

    try {
      // Call the cloud function
      await adminManageSponsors({
        sessionId: session.id,
        targetUserId: userId,
        action: action,
        orgId: session.orgId
      });
      // Update local state immediately for responsiveness
      setAssignedSponsorIds(prev =>
        isCurrentlyAssigned ? prev.filter(id => id !== userId) : [...prev, userId]
      );
    } catch (err) {
      console.error("Error updating sponsors:", err);
      setError(`Failed to ${action} sponsor: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };
  // --- END OF REWRITE ---


  if (!isOpen) return null;

  // --- No changes to JSX ---
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <button
          onClick={onClose}
          disabled={isUpdating}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="text-xl font-semibold text-gray-900">Assign Session Sponsors</h2>
        <p className="mt-1 text-sm text-gray-500">Sponsors can manage packages, reviewers, and session status.</p>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-4 max-h-80 space-y-2 overflow-y-auto border-t border-b py-4">
          {isLoadingUsers ? (
            <div className="flex justify-center"><LoadingSpinner /></div>
          ) : orgUsers.length === 0 ? (
            <p className="text-center text-gray-500">No users found in your organization.</p>
          ) : (
            orgUsers.map(user => {
              const isAssigned = assignedSponsorIds.includes(user.id);
              return (
                <div key={user.id} className="flex items-center justify-between rounded-md p-2 hover:bg-gray-50">
                  <div>
                    <span className="font-medium text-gray-800">{user.displayName}</span>
                    <span className="ml-2 text-sm text-gray-500">({user.email})</span>
                    {user.role === 'admin' && <span className="ml-2 text-xs text-purple-600">(Admin)</span>}
                  </div>
                  <button
                    onClick={() => toggleSponsor(user.id)}
                    disabled={isUpdating}
                    className={`ml-4 flex flex-shrink-0 items-center rounded px-2 py-1 text-xs font-medium shadow-sm disabled:opacity-50 ${isAssigned
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                  >
                    {isUpdating ? <LoadingSpinner /> : (isAssigned ? <UserMinus className="mr-1 h-3 w-3" /> : <ShieldCheck className="mr-1 h-3 w-3" />)}
                    {isAssigned ? 'Remove' : 'Assign'}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isUpdating}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}